import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { App, Button, Form, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useFeedback } from '../utils/feedback';
import { authService } from '../services/auth';
import type { Product, ProductVariant } from '../types/pz';
import { getApiErrorMessage } from '../utils/apiError';
import {
  createProduct,
  deleteVariant,
  fetchProduct as fetchProductApi,
  updateProduct,
  type ProductFormValues,
} from '../modules/products/api';
import {
  canDeleteProductCatalogItem,
  canManageProductCatalog,
} from '../modules/products/productCatalogAccessPolicy';
import {
  ProductCategory,
  categoryErrorMessage,
  fetchProductCategoryTags,
} from '../modules/products/productCategoriesApi';
import {
  PALLET_PRESETS,
  getProductDataGaps,
  palletPresetFieldValues,
  toLogisticsFormValues,
  type PalletPresetId,
} from '../modules/products/productLogistics';
import ProductVariantModal from '../components/inventory/ProductVariantModal';
import { getProductVariantName } from '../utils/productSelectorLabel';
import IdentitySection from '../modules/products/card/IdentitySection';
import PieceSection from '../modules/products/card/PieceSection';
import PalletSection from '../modules/products/card/PalletSection';
import AverageCostSection from '../modules/products/card/AverageCostSection';
import VariantsSection from '../modules/products/card/VariantsSection';
import PalletSummaryPanel from '../modules/products/card/PalletSummaryPanel';
import CardSectionHeader from '../modules/products/card/CardSectionHeader';
import '../components/layout/AppPage.css';
import './ProductDetail.css';

// Stable backend codes of the average-cost rules (productService.applyAverageCostChanges).
const SAVE_ERROR_MESSAGES: Record<string, string> = {
  PRODUCT_AVERAGE_COST_UNIT_REQUIRED: 'products:card.errors.unitRequiredForVariants',
  PRODUCT_AVERAGE_COST_UNIT_CHANGED_WITH_VARIANTS: 'products:card.errors.unitChangedWithVariants',
  PRODUCT_VARIANT_AVERAGE_COST_UNKNOWN_VARIANT: 'products:card.errors.unknownVariant',
};

const NEW_PRODUCT_PALLET = PALLET_PRESETS.find((preset) => preset.id === 'eur')!;

const realVariantsOf = (product: Product | null) => (product?.variants || []).filter((variant) => !variant.isDefault);

export function toProductFormValues(product: Product | null): ProductFormValues {
  if (!product) {
    return {
      name: '',
      productCategoryId: null,
      isActive: true,
      palletCountsPackages: false,
      palletLength: NEW_PRODUCT_PALLET.lengthMm,
      palletWidth: NEW_PRODUCT_PALLET.widthMm,
      palletEmptyWeight: NEW_PRODUCT_PALLET.emptyWeightKg,
      palletStackLayers: 1,
      averagePurchasePricePLN: null,
      averagePurchasePriceUnit: 'm2',
      averagePurchasePriceCurrency: 'PLN',
      variantCosts: {},
    };
  }
  return {
    name: product.name,
    productCategoryId: product.productCategoryId ?? null,
    ean: product.ean ?? null,
    cnCode: product.cnCode ?? null,
    englishName: product.englishName ?? null,
    isActive: product.isActive !== false,
    ...toLogisticsFormValues(product),
    averagePurchasePricePLN: product.averagePurchasePricePLN ?? null,
    averagePurchasePriceUnit: product.averagePurchasePriceUnit ?? 'm2',
    averagePurchasePriceCurrency: product.averagePurchasePriceCurrency ?? 'PLN',
    variantCosts: Object.fromEntries(realVariantsOf(product)
      .map((variant) => [variant.id, variant.averagePurchasePricePLNOverride ?? null])),
  };
}

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['products', 'common']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [form] = Form.useForm<ProductFormValues>();
  const isNewProduct = id === 'new';

  const [loading, setLoading] = useState(!isNewProduct);
  const [saving, setSaving] = useState(false);
  const [product, setProduct] = useState<Product | null>(null);
  const [categoryTags, setCategoryTags] = useState<ProductCategory[]>([]);
  const [dirty, setDirty] = useState(false);
  const [preset, setPreset] = useState<PalletPresetId | null>(null);
  const [variantModalOpen, setVariantModalOpen] = useState(false);
  const [editingVariant, setEditingVariant] = useState<ProductVariant | null>(null);

  const currentUser = authService.getCurrentUser();
  const canEdit = canManageProductCatalog(currentUser);
  const canDeleteVariant = canDeleteProductCatalogItem(currentUser);
  const showCost = authService.canViewProductAverageCost();
  const canEditCost = authService.canEditProductAverageCost();

  const values = (Form.useWatch((all) => all, { form, preserve: true }) || {}) as ProductFormValues;
  const realVariants = useMemo(() => realVariantsOf(product), [product]);

  const applyProduct = useCallback((next: Product | null) => {
    form.resetFields();
    form.setFieldsValue(toProductFormValues(next));
    setDirty(false);
    setPreset(null);
  }, [form]);

  const loadProduct = useCallback(async ({ keepEdits = false, silent = false } = {}) => {
    if (!id || isNewProduct) return;
    if (!keepEdits && !silent) setLoading(true);
    try {
      const productData = await fetchProductApi(id);
      setProduct(productData);
      if (keepEdits) {
        // A variant was added/edited while the card has unsaved edits: keep
        // them and only register the new variants as inheriting the cost.
        const costs = { ...(form.getFieldValue('variantCosts') || {}) };
        realVariantsOf(productData).forEach((variant) => {
          if (!(variant.id in costs)) costs[variant.id] = variant.averagePurchasePricePLNOverride ?? null;
        });
        form.setFieldValue('variantCosts', costs);
      } else {
        applyProduct(productData);
      }
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'products:error.fetchDetailFailed'));
      navigate('/app/products');
    } finally {
      setLoading(false);
    }
  }, [id, isNewProduct, form, applyProduct, feedback, navigate, t]);

  useEffect(() => {
    fetchProductCategoryTags()
      .then(setCategoryTags)
      .catch(() => feedback.error(t('products:productCategory.loadFailed')));
    if (isNewProduct) {
      setProduct(null);
      applyProduct(null);
    } else {
      loadProduct();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleValuesChange = (changed: Partial<ProductFormValues>) => {
    setDirty(true);
    if ('palletLength' in changed || 'palletWidth' in changed) setPreset(null);
  };

  const handlePresetChange = (presetId: PalletPresetId) => {
    const presetValues = palletPresetFieldValues(presetId, values);
    if (!presetValues) return;
    form.setFieldsValue(presetValues);
    setPreset(presetId);
    setDirty(true);
  };

  const handleSave = async () => {
    try {
      await form.validateFields();
    } catch {
      feedback.warning(t('products:card.validation.fixFields'));
      return;
    }
    const formValues = form.getFieldsValue(true) as ProductFormValues;
    const name = String(formValues.name || '').trim();
    const options = {
      includeAverageCost: canEditCost,
      variantIds: canEditCost ? realVariants.map((variant) => variant.id) : [],
    };
    setSaving(true);
    try {
      if (isNewProduct) {
        const created = await createProduct({ ...formValues, name }, options);
        feedback.success(t('products:success.created', { name }));
        setDirty(false);
        navigate(`/app/products/${created.id}`);
      } else {
        await updateProduct(id!, { ...formValues, name }, options);
        feedback.success(t('products:success.updated', { name }));
        await loadProduct({ silent: true });
      }
    } catch (error: any) {
      feedback.error(categoryErrorMessage(
        error,
        t,
        getApiErrorMessage(error, t, 'products:error.saveFailed', SAVE_ERROR_MESSAGES, { name })
      ), { sticky: Boolean(SAVE_ERROR_MESSAGES[error?.response?.data?.code]) });
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (isNewProduct) {
      navigate('/app/products');
      return;
    }
    applyProduct(product);
  };

  const handleBack = () => {
    if (dirty && !isNewProduct) {
      feedback.warning(t('products:card.savebar.leaveBlocked'));
      return;
    }
    navigate('/app/products');
  };

  const handleDeactivateVariant = async (variant: ProductVariant) => {
    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('products:confirm.deleteVariant'),
        okText: t('products:variantEditor.deactivate'),
        cancelText: t('common:cancel'),
        okButtonProps: { danger: true },
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmed) return;

    const variantName = getProductVariantName(variant);
    try {
      await deleteVariant(variant.id);
      feedback.success(t('products:success.variantDeleted', { name: variantName }));
      await loadProduct({ keepEdits: dirty });
    } catch (error: any) {
      const code = error?.response?.data?.code;
      feedback.error(
        code === 'PRODUCT_VARIANT_HAS_STOCK'
          ? t('products:variantEditor.stockDeactivateBlocked')
          : t('products:error.deleteVariantFailed', { name: variantName }),
        { sticky: code === 'PRODUCT_VARIANT_HAS_STOCK' }
      );
    }
  };

  const gaps = getProductDataGaps(values, { includeCost: showCost });
  const savedUnit = product?.averagePurchasePriceUnit ?? null;
  const savedCurrency = product?.averagePurchasePriceCurrency ?? 'PLN';
  // Variant costs are amounts in the product unit and currency.
  const unitChangedWithVariantCosts = Boolean(savedUnit)
    && (values.averagePurchasePriceUnit !== savedUnit || (values.averagePurchasePriceCurrency || 'PLN') !== savedCurrency)
    && Object.values(values.variantCosts || {}).some((cost) => cost !== null && cost !== undefined);
  const disabled = !canEdit;

  return (
    <div className="app-page product-card-page">
      <Spin spinning={loading} size="large">
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleValuesChange}
        className="product-card-form"
        requiredMark={false}
      >
        <IdentitySection
          categories={categoryTags}
          disabled={disabled}
          isNew={isNewProduct}
          gaps={gaps}
          onBack={handleBack}
        />
        <div className="product-card-layout">
          <div className="product-card-stack">
            <PieceSection values={values} showHeight={values.height !== undefined} disabled={disabled} />
            <PalletSection values={values} disabled={disabled} preset={preset} onPresetChange={handlePresetChange} />
            {showCost && (
              <AverageCostSection
                values={values}
                disabled={disabled || !canEditCost}
                updatedByName={product?.averagePurchasePriceUpdatedByName}
                updatedAt={product?.averagePurchasePriceUpdatedAt}
                unitChangedWithVariantCosts={unitChangedWithVariantCosts}
              />
            )}
            {isNewProduct ? (
              <section className="product-card-section">
                <CardSectionHeader title={t('products:card.variants.title')} hint={t('products:card.variants.saveFirst')} />
              </section>
            ) : (
              <VariantsSection
                productEan={values.ean}
                variants={realVariants}
                showCost={showCost}
                canEdit={canEdit && canEditCost}
                canDeactivate={canDeleteVariant}
                productPrice={values.averagePurchasePricePLN}
                productUnit={values.averagePurchasePriceUnit}
                productCurrency={values.averagePurchasePriceCurrency || 'PLN'}
                onCostChanged={() => setDirty(true)}
                onAddVariant={() => { setEditingVariant(null); setVariantModalOpen(true); }}
                onEditVariant={(variant) => { setEditingVariant(variant); setVariantModalOpen(true); }}
                onDeactivateVariant={handleDeactivateVariant}
              />
            )}
          </div>
          <PalletSummaryPanel values={values} />
        </div>
      </Form>
      </Spin>

      {(dirty || isNewProduct) && canEdit && (
        <div className="product-card-savebar" role="region" aria-label={t('products:card.savebar.save')}>
          <span className="product-card-savebar__status">
            {isNewProduct ? t('products:card.savebar.new') : t('products:card.savebar.dirty')}
          </span>
          <Button onClick={handleDiscard} disabled={saving}>{t('products:card.savebar.discard')}</Button>
          <Button type="primary" onClick={handleSave} loading={saving} className="product-card-savebar__primary">
            {t('products:card.savebar.save')}
          </Button>
        </div>
      )}

      {product && (
        <ProductVariantModal
          open={variantModalOpen}
          product={product}
          variant={editingVariant}
          onCancel={() => setVariantModalOpen(false)}
          onSaved={async () => {
            setVariantModalOpen(false);
            setEditingVariant(null);
            await loadProduct({ keepEdits: dirty });
          }}
        />
      )}
    </div>
  );
};

export default ProductDetail;
